import moment from 'moment';
import {
  API_CLOSED,
  API_EXCLUSIVITY,
  API_FULL,
  API_NO_FLIGHT,
  API_NO_DEPARTURE,
  API_OPEN,
  API_SIBLING_SUMMER,
  API_SIBLING_WINTER,
} from 'Utils/api-availability/constants';
import graphQLClient from 'Utils/graphql-client/graphqlClient';
import {
  STATE_PRODUCT_CALENDAR,
  STATE_PRODUCT_BOOKABLE_DATES,
} from 'Utils/request/requestDictionary';
import { QUERY_DEPARTURE_DATE } from 'Utils/uri/constants';
import { getSpecificQueryParam } from 'Utils/uri/uriUtils';
import { momentFromApiDate } from 'Utils/date/apiDate';
const NO_TRANSPORT_VALUE = 'NO';
const DATE_FORMAT = 'YYYYMM';
export const INITIAL_MONTH = 0;
export type FetchCalendarOptions = {
  duration: number;
  resortId: string;
  transport: string;
  urgentisationRate: number;
};
export type CalendarData = {
  blocks: null | Array<{
    hasPrice: null | boolean;
    id: null | string;
    name: null | string;
    price: null | string;
    shortName: null | string;
    year: null | string;
  }>;
  firstAvailableMonthIndex: null | number;
  months: null | Array<{
    id: null | string;
    days: null | Array<{
      dayContent: null | {
        arrivalDate: null | string;
        availability: null | string;
        canGoToBooking: null | boolean;
        id: null | string;
        isCurrentMonth: null | boolean;
        name: null | string;
        number: null | string;
        offer: null | string;
        shortName: null | string;
        siblingId: null | string;
      };
      id?: null | string;
      priceConfig: null | {
        bestPrice: null | {
          formattedValue: null | string;
          value: number;
        };
        currency: null | string;
        initialPrice: null | {
          formattedValue: null | string;
        };
        isBestPriceMonth: null | boolean;
        termsAndConditions: null | {
          accommodationComfortLabel: null | string;
          departureCity: null | {
            value: null | string;
            name: null | string;
          };
          departureDate: null | string;
          duration: null | number;
          numberOfAdults: null | number;
          offerId?: null | string;
        };
        rawUnit: null | string;
        unit: null | string;
      };
      availabilities?: Array<{
        status: string;
        durations: Array<number>;
      }>;
    }>;
  }>;
  weekDays: null | Array<string>;
};
export type BookableDatesData = {
  months: null | Array<{
    id: null | string;
    days: null | Array<{
      id?: null | string;
      availabilities?: Array<{
        status: string;
        durations: Array<number>;
      }>;
    }>;
  }>;
};
export type PriceOfDay = {
  availability: string;
  available: boolean;
  closed: boolean;
  currency: string;
  departureCity: string;
  exclusivity: boolean;
  full: boolean;
  hasTransport: boolean;
  isBestPrice: boolean;
  lastAvailability: boolean;
  noAvailability: boolean;
  noDeparture: boolean;
  noFlight: boolean;
  oldPrice: string;
  price: string;
  priceValue: number;
  siblingId: string;
  siblingOpen: boolean;
  unit: string;
};
export type PricesByDays = Record<string, PriceOfDay>;
export type OpeningDate = {
  opening: string;
  closing: string;
};
export type ClosingDate = {
  after: Date;
  before: Date;
};
export type FetchBookableDatesOptions = {
  departureCity: string;
  productId: string;
};
export function calendarQuery(args: {
  departureCity: string;
  duration: number;
  locale: string;
  resortId: string;
  urgentisationRate: number;
}) {
  const { resortId, duration, departureCity, urgentisationRate } = args;
  return `
      calendar(resortId: "${resortId}", duration: "${duration}", departureCity: "${departureCity}") {
        blocks {
          hasPrice
          id
          name
          price {
            value
            currency
            formattedValue
          }
          shortName
          year
        }
        firstAvailableMonthIndex
        months {
          id
          days {
            dayContent {
              arrivalDate
              availability
              canGoToBooking
              id
              isCurrentMonth
              name
              number
              offer(urgentisationRate: ${urgentisationRate})
              shortName
              siblingId
            }
            priceConfig {
              bestPrice(forceAdultPerTrip: true, isCalendarCase: true) { value formattedValue }
              currency
              initialPrice(forceAdultPerTrip: true, isCalendarCase: true) { value formattedValue }
              isBestPriceMonth
              termsAndConditions {
                accommodationComfortLabel
                departureCity {
                  value
                  name
                }
                departureDate
                duration
                numberOfAdults
                offerId
              }
              rawUnit
              unit
            }
          }
        }
        weekDays
      }
    `;
}
export function bookableDatesQuery({
  departureCity,
  productId,
}: {
  departureCity: string;
  productId: string;
}) {
  return `
    bookableDates(productId: "${productId}", departureCity: "${departureCity}") {
      months {
        id
        days {
          id
          availabilities {
            status
            durations
          }
        }
      }
    }
  `;
}
export function queryFetchCalendar(
  appContext: AppContext,
  options: FetchCalendarOptions,
): Promise<{
  calendar: CalendarData;
}> {
  const { locale } = appContext;
  const { resortId = '', transport = '', duration, urgentisationRate } = options;
  const calendarQueryArgs = {
    locale,
    resortId,
    departureCity: transport || NO_TRANSPORT_VALUE,
    duration,
    urgentisationRate,
  };
  return graphQLClient
    .runQuery(`query {${calendarQuery(calendarQueryArgs)}}`, appContext, {
      requestName: STATE_PRODUCT_CALENDAR,
    })
    .then(({ calendar }) => ({
      calendar,
    }));
}
export function queryFetchBookableDates(
  appContext: AppContext,
  options: FetchBookableDatesOptions,
): Promise<{
  bookableDates: CalendarData['months'];
}> {
  const { productId = '', departureCity = '' } = options;
  const bookableDatesQueryArs = {
    productId,
    departureCity,
  };
  return graphQLClient
    .runQuery(`query {${bookableDatesQuery(bookableDatesQueryArs)}}`, appContext, {
      requestName: STATE_PRODUCT_BOOKABLE_DATES,
    })
    .then(({ bookableDates }) => ({
      bookableDates,
    }));
}
export function getDefaultSelectedMonth(
  value: string | number | null = null,
  format: string | null = null,
) {
  const momentDate = value ? moment(value, format) : moment();
  return parseInt(momentDate.format(DATE_FORMAT), 10);
}
export function getSelectedMonth(calendar: CalendarData, previousSelectedMonth: number): number {
  // The query date param prevails
  const dateQueryParam = getSpecificQueryParam(QUERY_DEPARTURE_DATE);

  if (dateQueryParam) {
    return getDefaultSelectedMonth(dateQueryParam);
  }

  const firstCalendarMonth = calendar.months?.[0].id || getDefaultSelectedMonth();
  const firstAvailableMonthIndex = calendar.firstAvailableMonthIndex || INITIAL_MONTH;
  const firstAvailableMonth = parseInt(
    moment(firstCalendarMonth, DATE_FORMAT)
      .add(firstAvailableMonthIndex, 'months')
      .format(DATE_FORMAT),
    10,
  );

  // The previously selected month remains selected unless the first available dates are later
  if (previousSelectedMonth > firstAvailableMonth) {
    return previousSelectedMonth;
  }

  return firstAvailableMonth;
}
export function getPricesByDays(calendar: CalendarData): PricesByDays {
  const prices = {};

  if (calendar.months) {
    calendar.months.forEach(({ days }) => {
      if (days) {
        days.forEach(({ dayContent, priceConfig }) => {
          const { availability, id, offer, siblingId } = dayContent || {};
          const isBestPrice = priceConfig?.isBestPriceMonth;
          const unit = priceConfig?.unit;
          const departureCityValue =
            priceConfig?.termsAndConditions?.departureCity?.value || NO_TRANSPORT_VALUE;

          if (id) {
            const available = availability === API_OPEN;
            const closed = availability === API_CLOSED;
            const exclusivity = availability === API_EXCLUSIVITY;
            const full = availability === API_FULL;
            const lastAvailability = offer === 'urgentisation';
            const noAvailability = availability === '';
            const noDeparture = availability === API_NO_DEPARTURE;
            const noFlight = availability === API_NO_FLIGHT;
            const siblingOpen =
              availability === API_SIBLING_SUMMER || availability === API_SIBLING_WINTER;
            prices[id] = {
              availability,
              available,
              closed,
              currency: priceConfig?.currency,
              departureCity: priceConfig?.termsAndConditions?.departureCity?.name || '',
              exclusivity,
              full,
              hasTransport: departureCityValue !== NO_TRANSPORT_VALUE,
              isBestPrice,
              lastAvailability,
              noAvailability,
              noDeparture,
              noFlight,
              oldPrice: priceConfig?.initialPrice?.formattedValue || null,
              price: priceConfig?.bestPrice?.formattedValue || null,
              priceValue: priceConfig?.bestPrice?.value || null,
              rate: priceConfig?.termsAndConditions?.offerId,
              siblingId,
              siblingOpen,
              unit,
            };
          }
        });
      }
    });
  }

  return prices;
}
export const getClosingDatesRanges = (openingDatesRanges: Array<OpeningDate>): Array<ClosingDate> =>
  openingDatesRanges
    .sort((date1, date2) => Number(date1.opening) - Number(date2.opening))
    .map((openingDate, index, openingDates) => ({
      after: momentFromApiDate(openingDate.closing).toDate(),
      before: momentFromApiDate(
        openingDates[index + 1]?.opening || openingDates[0].opening,
      ).toDate(),
    }));
